import {
  downloadTargetFile,
  getTranslateFileUrl,
  type DownloadTranslateFileType,
} from '@/app/services/translation';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { downloadFile } from '@/lib/download-utils';
import { getDownloadItems } from '@/lib/download-items';
import {
  runTranslationDownload,
  type TranslationDownloadType,
} from '@/lib/translation-download';
import { cn } from '@/lib/utils';
import {
  Columns2Icon,
  DownloadIcon,
  FileDownIcon,
  LanguagesIcon,
  Loader2Icon,
  MoreVerticalIcon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const DownloadButton = ({
  lang,
  sid,
  filename,
  title,
  compact = false,
  allowedTypes,
  transformBlob,
  onSuccess,
  triggerClassName,
  contentClassName,
  itemClassName,
  icon = 'download',
  showItemIcons = true,
  onOpenChange,
}: {
  lang: string;
  sid: string;
  filename: string;
  title?: string | null;
  compact?: boolean;
  allowedTypes?: DownloadTranslateFileType[];
  transformBlob?: (
    blob: Blob,
    fileName: string,
    type: TranslationDownloadType,
  ) => Blob | Promise<Blob>;
  onSuccess?: (fileName: string) => void;
  triggerClassName?: string;
  contentClassName?: string;
  itemClassName?: string;
  icon?: 'download' | 'more';
  showItemIcons?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [downloadLoading, setDownloadLoading] = useState(false);
  const downloadItems = getDownloadItems(filename).filter(
    (item) => !allowedTypes || allowedTypes.includes(item.key),
  );

  const renderItemIcon = (type: DownloadTranslateFileType) => {
    switch (type) {
      case 'source':
        return <FileDownIcon className="h-4 w-4" />;
      case 'target':
        return <LanguagesIcon className="h-4 w-4" />;
      case 'dual':
        return <Columns2Icon className="h-4 w-4" />;
    }
  };

  const onDownload = async (id: string, type: TranslationDownloadType) => {
    setDownloadLoading(true);
    try {
      await runTranslationDownload({
        sessionId: id,
        sourceFileName: filename,
        title,
        targetLang: lang,
        type,
        getTranslateFileUrl,
        downloadTargetFile,
        downloadFile,
        transformBlob,
        onSuccess,
      });
    } catch (error) {
      console.error(error);
      toast.error('下载失败，请稍后重试', { position: 'top-center' });
    }
    setDownloadLoading(false);
  };

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant={compact ? 'ghost' : 'outline'}
          size={compact ? 'icon' : 'sm'}
          className={cn(
            compact
              ? 'h-8 w-8 rounded-full bg-muted/70 hover:bg-foreground/10 dark:bg-white/8 dark:hover:bg-white/18'
              : 'w-full md:w-auto',
            triggerClassName,
          )}
          disabled={downloadLoading}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          {downloadLoading ? (
            <Loader2Icon className="w-4 h-4 animate-spin" />
          ) : icon === 'more' ? (
            <MoreVerticalIcon className="h-3.5 w-3.5" />
          ) : (
            <DownloadIcon className="w-4 h-4" />
          )}
          {!compact && '下载'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={cn('w-36 rounded-2xl p-2 shadow-sm', contentClassName)}
      >
        {downloadItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-2">
            <Loader2Icon className="w-4 h-4 animate-spin" />
            <p className="text-sm">正在获取下载链接...</p>
          </div>
        ) : (
          downloadItems.map((item) => (
            <DropdownMenuItem
              key={item.key}
              className={cn('rounded-xl', itemClassName)}
              onClick={(e) => {
                e.stopPropagation();
                void onDownload(sid, item.key);
              }}
            >
              {showItemIcons && renderItemIcon(item.key)}
              {item.label}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DownloadButton;
